import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  ArrowsClockwise,
  MagnifyingGlass,
  Package,
  CheckCircle,
  Clock,
  ChartLine,
  CaretRight,
} from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProductionTracking = () => {
  const navigate = useNavigate();
  const [trackingData, setTrackingData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    fetchTrackingData();
  }, []);

  const fetchTrackingData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/reports/production-tracking`);
      setTrackingData(response.data);
    } catch (error) {
      console.error("Error fetching production tracking data:", error);
      toast.error("Failed to load production tracking data");
    } finally {
      setLoading(false);
    }
  };

  const filteredData = trackingData.filter((item) => {
    const matchesSearch =
      item.work_order_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.item_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "all" || item.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const totalWOs = filteredData.length;
  const withActuals = filteredData.filter((d) => {
    const p = d.process_actuals || {};
    return Object.values(p).some((proc) => proc?.actual_sheet_counting || proc?.actual_board_counting);
  }).length;
  const inProgress = filteredData.filter((d) => d.status === "in_progress").length;
  const completed = filteredData.filter((d) => d.status === "completed" || d.status === "delivered").length;

  const getStatusBadge = (status) => {
    const styles = {
      completed: "bg-emerald-50 text-emerald-700 border border-emerald-200",
      delivered: "bg-emerald-50 text-emerald-700 border border-emerald-200",
      in_progress: "bg-amber-50 text-amber-700 border border-amber-200",
      available: "bg-slate-50 text-slate-700 border border-slate-200",
    };
    return styles[status] || styles.available;
  };

  const handleRowClick = (woNumber) => {
    navigate(`/admin/production-tracking/${woNumber}`);
  };

  return (
    <div className="min-h-screen bg-slate-50 font-body">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-8 py-5 flex items-center justify-between">
          <div>
            <h1 className="font-heading text-2xl font-medium tracking-tight text-slate-900">
              Production Tracking
            </h1>
            <p className="text-sm text-slate-500 mt-0.5">Select a work order to view material loss analysis</p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={fetchTrackingData}
              disabled={loading}
              className="rounded-sm border-slate-200 text-slate-700 hover:bg-slate-50"
              data-testid="refresh-btn"
            >
              <ArrowsClockwise className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-8 py-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white border border-slate-200 p-5" data-testid="stat-total-wo">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[10px] uppercase tracking-[0.15em] font-medium text-slate-500">Total Work Orders</p>
                <p className="font-mono text-3xl tracking-tighter text-slate-900 mt-1">{totalWOs}</p>
              </div>
              <div className="p-2 bg-slate-100 rounded-sm">
                <Package className="h-5 w-5 text-slate-600" weight="regular" />
              </div>
            </div>
          </div>

          <div className="bg-white border border-slate-200 p-5" data-testid="stat-with-actuals">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[10px] uppercase tracking-[0.15em] font-medium text-slate-500">With Actual Data</p>
                <p className="font-mono text-3xl tracking-tighter text-emerald-700 mt-1">{withActuals}</p>
              </div>
              <div className="p-2 bg-emerald-50 rounded-sm">
                <CheckCircle className="h-5 w-5 text-emerald-600" weight="regular" />
              </div>
            </div>
          </div>

          <div className="bg-white border border-slate-200 p-5" data-testid="stat-in-progress">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[10px] uppercase tracking-[0.15em] font-medium text-slate-500">In Progress</p>
                <p className="font-mono text-3xl tracking-tighter text-amber-700 mt-1">{inProgress}</p>
              </div>
              <div className="p-2 bg-amber-50 rounded-sm">
                <Clock className="h-5 w-5 text-amber-600" weight="regular" />
              </div>
            </div>
          </div>

          <div className="bg-white border border-slate-200 p-5" data-testid="stat-completed">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[10px] uppercase tracking-[0.15em] font-medium text-slate-500">Completed</p>
                <p className="font-mono text-3xl tracking-tighter text-slate-900 mt-1">{completed}</p>
              </div>
              <div className="p-2 bg-slate-100 rounded-sm">
                <ChartLine className="h-5 w-5 text-slate-600" weight="regular" />
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white border border-slate-200 p-4 mb-6 flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search by WO#, customer, or item..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 rounded-sm border-slate-200 focus:ring-2 focus:ring-slate-900 focus:ring-offset-2"
              data-testid="search-input"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full sm:w-[180px] rounded-sm border-slate-200" data-testid="status-filter">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent className="rounded-sm">
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="available">Available</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="delivered">Delivered</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Work Order List */}
        <div className="bg-white border border-slate-200">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <ArrowsClockwise className="h-8 w-8 animate-spin text-slate-400" />
            </div>
          ) : filteredData.length === 0 ? (
            <div className="text-center py-16 text-slate-500">
              <Package className="h-12 w-12 mx-auto mb-4 text-slate-300" weight="light" />
              <p className="font-medium">No work orders found</p>
              <p className="text-sm mt-1">Try adjusting your search or filter criteria</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="sticky top-0 bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-[10px] uppercase tracking-[0.15em] font-medium text-slate-500">WO Number</th>
                    <th className="px-4 py-3 text-left text-[10px] uppercase tracking-[0.15em] font-medium text-slate-500">Customer</th>
                    <th className="px-4 py-3 text-left text-[10px] uppercase tracking-[0.15em] font-medium text-slate-500">Item</th>
                    <th className="px-4 py-3 text-left text-[10px] uppercase tracking-[0.15em] font-medium text-slate-500">Order Qty</th>
                    <th className="px-4 py-3 text-left text-[10px] uppercase tracking-[0.15em] font-medium text-slate-500">Status</th>
                    <th className="px-4 py-3 text-left text-[10px] uppercase tracking-[0.15em] font-medium text-slate-500">Data Status</th>
                    <th className="w-10 px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredData.map((item) => {
                    const hasActualData = Object.values(item.process_actuals || {}).some(
                      (proc) => proc?.actual_sheet_counting || proc?.actual_board_counting
                    );

                    return (
                      <tr
                        key={item.work_order_number}
                        className="border-b border-slate-200 hover:bg-slate-50 cursor-pointer transition-colors"
                        onClick={() => handleRowClick(item.work_order_number)}
                        data-testid={`wo-row-${item.work_order_number}`}
                      >
                        <td className="px-4 py-4 font-mono text-sm font-medium text-slate-900">{item.work_order_number}</td>
                        <td className="px-4 py-4 text-sm text-slate-600">{item.customer_name || "—"}</td>
                        <td className="px-4 py-4 text-sm text-slate-600 max-w-[200px] truncate">{item.item_name || "—"}</td>
                        <td className="px-4 py-4 text-sm font-mono text-slate-600">{item.order_qty || "—"}</td>
                        <td className="px-4 py-4">
                          <span className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-wider font-medium ${getStatusBadge(item.status)}`}>
                            {item.status?.replace("_", " ") || "available"}
                          </span>
                        </td>
                        <td className="px-4 py-4">
                          {hasActualData ? (
                            <span className="inline-flex items-center gap-1 text-xs text-emerald-600">
                              <CheckCircle className="h-4 w-4" weight="fill" />
                              Has Data
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-xs text-amber-600">
                              <Clock className="h-4 w-4" weight="regular" />
                              Awaiting
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-4">
                          <CaretRight className="h-5 w-5 text-slate-400" />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default ProductionTracking;
